import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, User, LogOut, ChevronDown, Sparkles, RefreshCw } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { getInitials } from '../../utils/helpers';

const Header = ({ onNotificationClick }) => {
  const { user, logout, restoreAdmin } = useAuth();
  const { unreadCount } = useNotifications();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleRestoreAdmin = async () => {
    await restoreAdmin();
    navigate('/admin');
  };

  // Check if admin is in switch user mode
  const isSwitchedUser = user?.role !== 'admin' && localStorage.getItem('eureka_original_admin');

  return (
    <header className="glass-effect border-b-2 border-white/30 sticky top-0 z-40 shadow-xl backdrop-blur-xl">
      <div className="flex items-center justify-between px-6 py-4">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-3 group">
            <div className="w-12 h-12 bg-gradient-to-br from-primary-500 via-secondary-500 to-accent-500 rounded-2xl flex items-center justify-center shadow-lg shadow-primary-500/50 transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 animate-float">
              <Sparkles className="text-white w-7 h-7" />
            </div>
            <div>
              <h1 className="text-2xl font-black bg-gradient-to-r from-primary-600 via-secondary-600 to-accent-600 bg-clip-text text-transparent">
                EUREKA
              </h1>
              <p className="text-xs text-gray-600 font-bold uppercase tracking-wider">RD Garantía</p>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          {/* Switch User Indicator */}
          {isSwitchedUser && (
            <div className="flex items-center space-x-2 px-3 py-2 bg-yellow-50 border border-yellow-200 rounded-lg">
              <RefreshCw className="w-4 h-4 text-yellow-600" />
              <span className="text-sm text-yellow-800 font-medium">
                Modo: {user?.role.replace('_', ' ')}
              </span>
              <button
                onClick={handleRestoreAdmin}
                className="ml-2 px-2 py-1 text-xs bg-yellow-600 text-white rounded hover:bg-yellow-700 transition-colors"
              >
                Volver Admin
              </button>
            </div>
          )}

          <button
            onClick={onNotificationClick}
            className="relative p-3 text-gray-600 hover:text-white glass-effect hover:bg-gradient-to-r hover:from-primary-500 hover:to-secondary-500 rounded-xl transition-all duration-300 transform hover:scale-110 shadow-md hover:shadow-lg group"
          >
            <Bell className="w-6 h-6 group-hover:animate-wiggle" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-white bg-gradient-to-r from-danger-500 to-danger-600 rounded-full shadow-lg animate-pulse">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="relative flex items-center space-x-3 p-2 glass-effect hover:bg-gradient-to-r hover:from-primary-500 hover:to-secondary-500 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg group"
            >
              <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-all duration-300">
                {isSwitchedUser && (
                  <span className="absolute -top-1 -right-1 flex items-center justify-center w-6 h-6 bg-yellow-400 text-white rounded-full text-xs font-bold shadow-md">
                    ⚠️
                  </span>
                )}
                <span className="text-white font-bold text-sm">
                  {getInitials(user?.nombre)}
                </span>
              </div>
              <div className="text-left hidden md:block">
                <p className="text-sm font-bold text-gray-900 group-hover:text-white transition-colors">{user?.nombre}</p>
                <p className="text-xs text-gray-600 capitalize font-semibold group-hover:text-white/80 transition-colors">{user?.role}</p>
              </div>
              <ChevronDown className="w-4 h-4 text-gray-600 group-hover:text-white transition-colors" />
            </button>

            {showUserMenu && (
              <>
                {/* Si el usuario está en modo switch, mostramos una opción rápida
                    para restaurar al admin antes de renderizar el overlay y el menú.
                    El botón usa un z-index superior para permanecer clicable encima
                    del overlay (inset-0). */}
                {isSwitchedUser && (
                  <div className="absolute right-0 mt-2 z-30 pr-2">
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        handleRestoreAdmin();
                      }}
                      className="w-64 flex items-center px-4 py-3 text-sm font-semibold text-yellow-700 bg-yellow-50 rounded-xl shadow-md hover:bg-yellow-100 transition-all duration-300 relative z-30"
                    >
                      <div className="w-8 h-8 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-lg flex items-center justify-center mr-3 shadow-md">
                        <RefreshCw className="w-4 h-4 text-white" />
                      </div>
                      Volver a Admin
                    </button>
                  </div>
                )}
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowUserMenu(false)}
                ></div>
                <div className="absolute right-0 mt-2 w-64 glass-effect rounded-2xl shadow-2xl border-2 border-white/30 py-2 z-20 animate-slide-down overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary-500/10 via-secondary-500/10 to-accent-500/10"></div>
                  <div className="px-4 py-3 border-b border-white/20 relative z-10">
                    <p className="text-sm font-bold text-gray-900">{user?.nombre}</p>
                    <p className="text-xs text-gray-600 font-semibold">{user?.email}</p>
                  </div>
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      navigate('/perfil');
                    }}
                    className="w-full flex items-center px-4 py-3 text-sm font-semibold text-gray-700 hover:bg-gradient-to-r hover:from-primary-500 hover:to-secondary-500 hover:text-white transition-all duration-300 relative z-10 group"
                  >
                    <div className="w-8 h-8 bg-gradient-to-br from-primary-400 to-primary-600 rounded-lg flex items-center justify-center mr-3 shadow-md group-hover:scale-110 transition-transform">
                      <User className="w-4 h-4 text-white" />
                    </div>
                    Mi Perfil
                  </button>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center px-4 py-3 text-sm font-semibold text-danger-600 hover:bg-gradient-to-r hover:from-danger-500 hover:to-danger-600 hover:text-white transition-all duration-300 relative z-10 group"
                  >
                    <div className="w-8 h-8 bg-gradient-to-br from-danger-400 to-danger-600 rounded-lg flex items-center justify-center mr-3 shadow-md group-hover:scale-110 transition-transform">
                      <LogOut className="w-4 h-4 text-white" />
                    </div>
                    Cerrar Sesión
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
